'use client';

/**
 * 오디오 어레인지먼트 액션 훅
 *
 * 어레인지먼트 CRUD, 렌더링, 트랙 등록 등
 * 모든 어레인지먼트 관련 mutation을 하나의 훅으로 통합합니다.
 */

import { useQueryClient } from '@tanstack/react-query';
import {
  invalidateArrangementList,
  invalidateArrangement,
  invalidateArrangementAll,
  invalidatePlaylistTracks,
} from '@/lib/api/cacheUtils';
import {
  useAudioArrangementCreate,
  useAudioArrangementPartialUpdate,
  useAudioArrangementDestroy,
  useAudioArrangementRender,
  useAudioArrangementCreateTrack,
} from '@/lib/api/orval';
import type {
  AudioArrangementCreateRequest,
  PatchedAudioArrangementUpdateRequest,
} from '@/lib/api/orval/models';
import { createMutationErrorHandler } from '@/lib/utils/errorUtils';
import { useToast } from './useToast';

export interface UseAudioArrangementActionsOptions {
  playlistId: string;
  arrangementId?: string;
  onCreateSuccess?: (id: string) => void;
  onDeleteSuccess?: () => void;
  onRenderSuccess?: () => void;
  onCreateTrackSuccess?: () => void;
}

export function useAudioArrangementActions({
  playlistId,
  arrangementId,
  onCreateSuccess,
  onDeleteSuccess,
  onRenderSuccess,
  onCreateTrackSuccess,
}: UseAudioArrangementActionsOptions) {
  const queryClient = useQueryClient();
  const { success: toastSuccess, error: toastError } = useToast();

  // 어레인지먼트 생성
  const createMutation = useAudioArrangementCreate({
    mutation: {
      onSuccess: (data) => {
        invalidateArrangementList(queryClient, playlistId);
        toastSuccess('어레인지먼트가 생성되었습니다.');
        onCreateSuccess?.(data.id);
      },
      onError: createMutationErrorHandler(toastError, '어레인지먼트 생성 실패'),
    },
  });

  // 어레인지먼트 수정
  const updateMutation = useAudioArrangementPartialUpdate({
    mutation: {
      onSuccess: () => {
        if (arrangementId) {
          invalidateArrangementAll(queryClient, arrangementId, playlistId);
        }
        toastSuccess('어레인지먼트가 수정되었습니다.');
      },
      onError: createMutationErrorHandler(toastError, '어레인지먼트 수정 실패'),
    },
  });

  // 어레인지먼트 삭제
  const deleteMutation = useAudioArrangementDestroy({
    mutation: {
      onSuccess: () => {
        invalidateArrangementList(queryClient, playlistId);
        toastSuccess('어레인지먼트가 삭제되었습니다.');
        onDeleteSuccess?.();
      },
      onError: createMutationErrorHandler(toastError, '어레인지먼트 삭제 실패'),
    },
  });

  // 렌더링 시작
  const renderMutation = useAudioArrangementRender({
    mutation: {
      onSuccess: () => {
        if (arrangementId) {
          invalidateArrangement(queryClient, arrangementId);
        }
        toastSuccess('렌더링이 시작되었습니다. 잠시 기다려주세요.');
        onRenderSuccess?.();
      },
      onError: createMutationErrorHandler(toastError, '렌더링 시작 실패'),
    },
  });

  // 렌더링 결과를 새 트랙으로 등록
  const createTrackMutation = useAudioArrangementCreateTrack({
    mutation: {
      onSuccess: () => {
        invalidatePlaylistTracks(queryClient, playlistId);
        if (arrangementId) {
          invalidateArrangement(queryClient, arrangementId);
        }
        toastSuccess('트랙이 등록되었습니다.');
        onCreateTrackSuccess?.();
      },
      onError: createMutationErrorHandler(toastError, '트랙 등록 실패'),
    },
  });

  // 핸들러 함수들
  const handleCreate = async (data: AudioArrangementCreateRequest) => {
    return await createMutation.mutateAsync({ data });
  };

  const handleUpdate = async (id: string, data: PatchedAudioArrangementUpdateRequest) => {
    await updateMutation.mutateAsync({ id, data });
  };

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync({ id });
  };

  const handleRender = async (id: string) => {
    await renderMutation.mutateAsync({ id });
  };

  const handleCreateTrack = async (id: string, data?: { title?: string }) => {
    await createTrackMutation.mutateAsync({ id, data: data ?? {} });
  };

  return {
    // 생성/수정/삭제
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    handleCreate,
    handleUpdate,
    handleDelete,

    // 렌더링
    isRendering: renderMutation.isPending,
    handleRender,

    // 트랙 등록
    isCreatingTrack: createTrackMutation.isPending,
    handleCreateTrack,
  };
}

export type UseAudioArrangementActionsReturn = ReturnType<typeof useAudioArrangementActions>;
